import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Button, Alert, LinearProgress,
  Stack, Chip, TextField, Stepper, Step, StepLabel, Select, MenuItem,
  FormControl, InputLabel, IconButton, Tooltip, Tabs, Tab,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import {
  Lightbulb as LightbulbIcon,
  ThumbUp as ThumbUpIcon,
  Send as SendIcon,
  AutoAwesome as AiIcon,
  ArrowBack as BackIcon,
  ArrowForward as ForwardIcon,
} from '@mui/icons-material';
import { featureRequestsApi } from '../api/services';

interface FeatureRequest {
  id: string;
  title: string;
  description: string;
  userStory?: string;
  acceptanceCriteria?: string;
  technicalNotes?: string;
  priority?: string;
  category?: string;
  status: string;
  adminNotes?: string;
  votes: number;
  submittedByName: string;
  submittedByEmail: string;
  createdAt: string;
  tenantId?: string;
}

// --- AI Story Builder helpers (client-side, no API call) ---

const detectCategory = (text: string): string => {
  const lower = text.toLowerCase();
  if (lower.includes('calendar')) return 'Calendar';
  if (lower.includes('work item') || lower.includes('task')) return 'WorkItems';
  if (lower.includes('report')) return 'Reports';
  if (lower.includes('dashboard')) return 'Dashboard';
  if (lower.includes('ea ') || lower.includes('assistant')) return 'EA';
  if (lower.includes('admin')) return 'Admin';
  return 'General';
};

const generateUserStory = (
  idea: string,
  who: string,
  frequency: string,
  importance: string,
) => {
  const role =
    who === 'Executive'
      ? 'an executive'
      : who === 'EA'
        ? 'an executive assistant'
        : 'an administrator';
  const benefit =
    frequency === 'Daily'
      ? 'save time every day'
      : frequency === 'Weekly'
        ? 'improve my weekly workflow'
        : 'have this capability when needed';

  return {
    userStory: `As ${role}, I want to ${idea.toLowerCase()}, so that I can ${benefit}.`,
    acceptanceCriteria:
      `- The feature should be accessible from the ${detectCategory(idea)} page\n` +
      `- It should work for all user roles that need it\n` +
      `- It should respect the current theme (light/dark)\n` +
      `- It should be responsive on mobile`,
    technicalNotes: `Category: ${detectCategory(idea)}\nFrequency: ${frequency}\nRequested by: ${who}`,
    priority:
      importance === 'Critical'
        ? 'High'
        : importance === 'Important'
          ? 'Medium'
          : 'Low',
    category: detectCategory(idea),
  };
};

const statusColors: Record<string, 'default' | 'info' | 'warning' | 'success' | 'error' | 'primary'> = {
  Submitted: 'default',
  UnderReview: 'info',
  Planned: 'primary',
  InProgress: 'warning',
  Completed: 'success',
  Declined: 'error',
};

const statusLabels: Record<string, string> = {
  Submitted: 'Submitted',
  UnderReview: 'Under Review',
  Planned: 'Planned',
  InProgress: 'In Progress',
  Completed: 'Completed',
  Declined: 'Declined',
};

const categories = ['All', 'Dashboard', 'Calendar', 'WorkItems', 'Reports', 'EA', 'Admin', 'General'];
const statuses = ['All', 'Submitted', 'UnderReview', 'Planned', 'InProgress', 'Completed', 'Declined'];

// --- Wizard Component ---

function SubmitWizard({ onSubmitted }: { onSubmitted: () => void }) {
  const [step, setStep] = useState(0);
  const [idea, setIdea] = useState('');
  const [who, setWho] = useState('Executive');
  const [frequency, setFrequency] = useState('Weekly');
  const [importance, setImportance] = useState('Important');
  const [generated, setGenerated] = useState<ReturnType<typeof generateUserStory> | null>(null);
  const [title, setTitle] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleGenerate = () => {
    const result = generateUserStory(idea, who, frequency, importance);
    setGenerated(result);
    // Auto-generate a title from the idea
    const autoTitle = idea.length > 200 ? idea.slice(0, 197) + '...' : idea;
    setTitle(autoTitle.charAt(0).toUpperCase() + autoTitle.slice(1));
    setStep(3);
  };

  const handleSubmit = async () => {
    if (!generated || !title.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      await featureRequestsApi.create({
        title: title.trim(),
        description: idea,
        userStory: generated.userStory,
        acceptanceCriteria: generated.acceptanceCriteria,
        technicalNotes: generated.technicalNotes,
        priority: generated.priority,
        category: generated.category,
      });
      setSuccess(true);
      setTimeout(() => {
        onSubmitted();
      }, 1500);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  const steps = ['Describe your idea', 'Clarifying questions', 'AI builds story', 'Review & submit'];

  return (
    <Card sx={{ bgcolor: 'background.paper' }}>
      <CardContent>
        <Stepper activeStep={step} alternativeLabel sx={{ mb: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Feature request submitted successfully! Redirecting to browse view...
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Step 0: Describe the idea */}
        {step === 0 && (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LightbulbIcon sx={{ color: 'warning.main' }} />
              What would you like Cadence to do?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Describe your idea in plain language. Be as specific or general as you like.
            </Typography>
            <TextField
              multiline
              rows={4}
              fullWidth
              placeholder="e.g. I'd like a way to see all my work items grouped by strategic driver on the dashboard..."
              value={idea}
              onChange={(e) => setIdea(e.target.value)}
              sx={{ mb: 2 }}
            />
            <Stack direction="row" justifyContent="flex-end">
              <Button
                variant="contained"
                endIcon={<ForwardIcon />}
                disabled={!idea.trim()}
                onClick={() => setStep(1)}
              >
                Next
              </Button>
            </Stack>
          </Box>
        )}

        {/* Step 1: Clarifying questions */}
        {step === 1 && (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AiIcon sx={{ color: 'primary.main' }} />
              A few quick questions to shape the story
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              These help us understand priority, audience, and scope.
            </Typography>

            <Stack spacing={3}>
              <FormControl fullWidth>
                <InputLabel>Who would primarily use this feature?</InputLabel>
                <Select
                  value={who}
                  label="Who would primarily use this feature?"
                  onChange={(e) => setWho(e.target.value)}
                >
                  <MenuItem value="Executive">Executive</MenuItem>
                  <MenuItem value="EA">Executive Assistant</MenuItem>
                  <MenuItem value="Admin">Administrator</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>How often would you use this?</InputLabel>
                <Select
                  value={frequency}
                  label="How often would you use this?"
                  onChange={(e) => setFrequency(e.target.value)}
                >
                  <MenuItem value="Daily">Daily</MenuItem>
                  <MenuItem value="Weekly">Weekly</MenuItem>
                  <MenuItem value="Monthly">Monthly</MenuItem>
                  <MenuItem value="Rarely">Rarely</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>How important is this to your workflow?</InputLabel>
                <Select
                  value={importance}
                  label="How important is this to your workflow?"
                  onChange={(e) => setImportance(e.target.value)}
                >
                  <MenuItem value="Nice to have">Nice to have</MenuItem>
                  <MenuItem value="Important">Important</MenuItem>
                  <MenuItem value="Critical">Critical</MenuItem>
                </Select>
              </FormControl>
            </Stack>

            <Stack direction="row" justifyContent="space-between" sx={{ mt: 3 }}>
              <Button startIcon={<BackIcon />} onClick={() => setStep(0)}>
                Back
              </Button>
              <Button
                variant="contained"
                endIcon={<AiIcon />}
                onClick={handleGenerate}
              >
                Generate Story
              </Button>
            </Stack>
          </Box>
        )}

        {/* Step 2 is auto-skipped (generation happens instantly) */}

        {/* Step 3: Review & submit */}
        {step === 3 && generated && (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AiIcon sx={{ color: 'success.main' }} />
              Review your feature request
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Edit anything before submitting. The AI-generated story is a starting point.
            </Typography>

            <Stack spacing={2.5}>
              <TextField
                label="Title"
                fullWidth
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                inputProps={{ maxLength: 200 }}
                helperText={`${title.length}/200`}
              />

              <TextField
                label="Original Idea"
                fullWidth
                multiline
                rows={2}
                value={idea}
                onChange={(e) => setIdea(e.target.value)}
              />

              <TextField
                label="User Story"
                fullWidth
                multiline
                rows={2}
                value={generated.userStory}
                onChange={(e) =>
                  setGenerated({ ...generated, userStory: e.target.value })
                }
              />

              <TextField
                label="Acceptance Criteria"
                fullWidth
                multiline
                rows={4}
                value={generated.acceptanceCriteria}
                onChange={(e) =>
                  setGenerated({ ...generated, acceptanceCriteria: e.target.value })
                }
              />

              <TextField
                label="Technical Notes"
                fullWidth
                multiline
                rows={2}
                value={generated.technicalNotes}
                onChange={(e) =>
                  setGenerated({ ...generated, technicalNotes: e.target.value })
                }
              />

              <Stack direction="row" spacing={2}>
                <Chip label={`Priority: ${generated.priority}`} color="primary" variant="outlined" />
                <Chip label={`Category: ${generated.category}`} color="secondary" variant="outlined" />
              </Stack>
            </Stack>

            <Stack direction="row" justifyContent="space-between" sx={{ mt: 3 }}>
              <Button startIcon={<BackIcon />} onClick={() => setStep(1)}>
                Back
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<SendIcon />}
                onClick={handleSubmit}
                disabled={submitting || !title.trim() || success}
              >
                {submitting ? 'Submitting...' : 'Submit Feature Request'}
              </Button>
            </Stack>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

// --- Browse Component ---

function BrowseRequests() {
  const [requests, setRequests] = useState<FeatureRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');
  const [votingId, setVotingId] = useState<string | null>(null);

  const loadRequests = useCallback(async () => {
    setLoading(true);
    try {
      const data = await featureRequestsApi.getAll();
      setRequests(data);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load requests');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  const handleVote = async (id: string) => {
    setVotingId(id);
    try {
      const result = await featureRequestsApi.vote(id);
      setRequests((prev) =>
        prev.map((r) => (r.id === id ? { ...r, votes: result.votes } : r)),
      );
    } catch {
      // vote failed silently
    } finally {
      setVotingId(null);
    }
  };

  const filtered = requests.filter((r) => {
    if (categoryFilter !== 'All' && r.category !== categoryFilter) return false;
    if (statusFilter !== 'All' && r.status !== statusFilter) return false;
    return true;
  });

  if (loading) return <LinearProgress />;

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Category</InputLabel>
          <Select
            value={categoryFilter}
            label="Category"
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            {categories.map((c) => (
              <MenuItem key={c} value={c}>
                {c}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            {statuses.map((s) => (
              <MenuItem key={s} value={s}>
                {statusLabels[s] || s}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {filtered.length === 0 ? (
        <Card sx={{ bgcolor: 'background.paper' }}>
          <CardContent>
            <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
              No feature requests found. Be the first to submit one!
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Stack spacing={2}>
          {filtered.map((req) => (
            <Card key={req.id} sx={{ bgcolor: 'background.paper' }}>
              <CardContent>
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  {/* Vote button */}
                  <Box sx={{ textAlign: 'center', minWidth: 60 }}>
                    <Tooltip title="Vote for this feature">
                      <IconButton
                        onClick={() => handleVote(req.id)}
                        disabled={votingId === req.id}
                        color="primary"
                        size="small"
                      >
                        <ThumbUpIcon />
                      </IconButton>
                    </Tooltip>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                      {req.votes}
                    </Typography>
                  </Box>

                  {/* Content */}
                  <Box sx={{ flex: 1 }}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                        {req.title}
                      </Typography>
                      <Chip
                        label={statusLabels[req.status] || req.status}
                        size="small"
                        color={statusColors[req.status] || 'default'}
                        variant="outlined"
                      />
                      {req.category && (
                        <Chip label={req.category} size="small" variant="outlined" />
                      )}
                      {req.priority && (
                        <Chip label={req.priority} size="small" variant="outlined" color="warning" />
                      )}
                    </Stack>

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {req.description}
                    </Typography>

                    {req.userStory && (
                      <Typography
                        variant="body2"
                        sx={{
                          fontStyle: 'italic',
                          color: 'text.secondary',
                          bgcolor: 'action.hover',
                          p: 1,
                          borderRadius: 1,
                          mb: 1,
                        }}
                      >
                        {req.userStory}
                      </Typography>
                    )}

                    <Stack direction="row" spacing={2} alignItems="center">
                      <Typography variant="caption" color="text.secondary">
                        By {req.submittedByName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(req.createdAt).toLocaleDateString()}
                      </Typography>
                    </Stack>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}
    </Box>
  );
}

// --- Main Page ---

export default function FeatureRequestPage() {
  const [tab, setTab] = useState(0);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <LightbulbIcon sx={{ color: 'warning.main', fontSize: 28 }} />
        <Box>
          <Typography variant="h4">Feature Requests</Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Submit ideas for Cadence and vote on community requests
          </Typography>
        </Box>
      </Box>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ mb: 3 }}
      >
        <Tab label="Browse Requests" />
        <Tab label="Submit New Idea" />
      </Tabs>

      {tab === 0 && <BrowseRequests />}
      {tab === 1 && <SubmitWizard onSubmitted={() => setTab(0)} />}
    </Box>
  );
}
